import React, { Component, ErrorInfo } from 'react'
import { ThemeProvider, useTheme } from 'styled-components'
import { ApolloError } from 'apollo-client'
import * as Sentry from '@sentry/browser'
import { DynamicGlobalStyles, lightTheme } from '@sketch/global-styles'

import GenericErrorState from './GenericErrorState'

const ThemeProviderFallback: React.FC<{}> = ({ children }) => {
  const theme = useTheme()

  if (!theme) {
    return (
      <ThemeProvider theme={lightTheme}>
        <DynamicGlobalStyles userAgent="" />
        {children}
      </ThemeProvider>
    )
  }

  return <>{children}</>
}

type ErrorTypes = string | ApolloError

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallbackComponent?: React.ComponentType<{ error: ErrorTypes }>
}

interface ErrorBoundaryState {
  error: ErrorTypes | null
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.withScope(scope => {
      scope.setExtra('ErrorBoundary', info)
      Sentry.captureException(error)
    })
  }

  render() {
    const { fallbackComponent: FallbackComponent } = this.props
    const { error } = this.state

    if (error) {
      const Component = FallbackComponent || GenericErrorState

      return (
        <ThemeProviderFallback>
          <Component error={error} />
        </ThemeProviderFallback>
      )
    }

    return this.props.children
  }
}
